import React, { useState, useEffect, useRef } from "react";

interface WavePoint {
  x: number;
  y: number;
  vy: number;
  targetY: number;
}

interface MousePosition {
  x: number;
  y: number;
}

interface WaveClipProps {
  children: React.ReactNode;
}

const WaveClip: React.FC<WaveClipProps> = ({ children }) => {
  const [wavePath, setWavePath] = useState<string>("");
  const [mousePos, setMousePos] = useState<MousePosition>({ x: 0, y: 0 });
  const requestRef = useRef<number | null>(null);
  const mobileMouseRef = useRef<number | null>(null);
  const cursorRafRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const sizeRef = useRef({ width: typeof window !== "undefined" ? window.innerWidth : 1, height: typeof window !== "undefined" ? window.innerHeight : 1 });
  const mousePosRef = useRef<MousePosition>({ x: 0, y: 0 });
  const pointsRef = useRef<WavePoint[]>([]);

  const initializePoints = (width: number, height: number) => {
    const isMobile = width < 768;
    const numPoints = isMobile ? 10 : 18; // fewer points for Safari perf
    const baseline = height * 0.55;

    pointsRef.current = Array.from({ length: numPoints }, (_, i) => ({
      x: (width * i) / (numPoints - 1),
      y: baseline,
      vy: 0,
      targetY: baseline,
    }));
    setWavePath(createWavePath(pointsRef.current, width, height));
  };

  // Measure container and build initial points
  useEffect(() => {
    const measure = () => {
      const rect = containerRef.current?.getBoundingClientRect();
      const width = rect?.width ?? window.innerWidth;
      const height = rect?.height ?? window.innerHeight;
      sizeRef.current = { width, height };
      initializePoints(width, height);
    };

    // ensure ref is available
    const id = requestAnimationFrame(measure);
    window.addEventListener("resize", measure);

    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener("resize", measure);
    };
  }, []);

  // On small screens, drive the wave with a gentle automatic motion
  useEffect(() => {
    const isMobile = sizeRef.current.width < 768;
    if (!isMobile) return;

    const startTime = performance.now();

    const tick = (now: number) => {
      const seconds = (now - startTime) / 1000;
      const { width, height } = sizeRef.current;
      const centerX = width / 2;
      const centerY = height / 2;
      const x = centerX + Math.sin(seconds * 1.2) * (width / 3);
      const y = centerY + Math.cos(seconds * 0.9) * (height / 6);
      mousePosRef.current = { x, y };
      if (!cursorRafRef.current) {
        cursorRafRef.current = requestAnimationFrame(() => {
          setMousePos({ x, y });
          cursorRafRef.current = null;
        });
      }
      mobileMouseRef.current = requestAnimationFrame(tick);
    };

    mobileMouseRef.current = requestAnimationFrame(tick);

    return () => {
      if (mobileMouseRef.current) cancelAnimationFrame(mobileMouseRef.current);
    };
  }, []);

  const animate = () => {
    const currentPoints = pointsRef.current;
    if (!currentPoints.length) {
      requestRef.current = requestAnimationFrame(animate);
      return;
    }

    const { width, height } = sizeRef.current;
    const isMobile = width < 768;
    const radius = isMobile ? 120 : 200;
    const spring = isMobile ? 0.065 : 0.08;
    const damping = isMobile ? 0.88 : 0.85;
    const spread = isMobile ? 0.2 : 0.25;
    const baseline = height * 0.55;
    const { x: mx, y: my } = mousePosRef.current;

    for (let i = 0; i < currentPoints.length; i += 1) {
      const point = currentPoints[i];
      const dx = mx - point.x;
      const dy = my - point.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      let newTargetY = baseline;
      if (distance < radius) {
        const force = (1 - distance / radius) * (isMobile ? 95 : 150);
        newTargetY += force * (my > point.y ? -1 : 1);
      }

      const leftY = i > 0 ? currentPoints[i - 1].y : baseline;
      const rightY = i < currentPoints.length - 1 ? currentPoints[i + 1].y : baseline;
      const neighborPull = ((leftY + rightY) / 2 - point.y) * spread;

      const acceleration = (newTargetY - point.y) * spring + neighborPull;
      point.vy = (point.vy + acceleration) * damping;
      point.y = point.y + point.vy;
      point.targetY = newTargetY;
    }

    // Update path string once per frame
    setWavePath(createWavePath(currentPoints, width, height));
    requestRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    requestRef.current = requestAnimationFrame(animate);
    return () => {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
      if (cursorRafRef.current) cancelAnimationFrame(cursorRafRef.current);
    };
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    mousePosRef.current = { x, y };
    if (!cursorRafRef.current) {
      cursorRafRef.current = requestAnimationFrame(() => {
        setMousePos({ x, y });
        cursorRafRef.current = null;
      });
    }
  };

  const createWavePath = (
    pts: WavePoint[],
    width: number,
    height: number
  ) => {
    if (pts.length < 2) return "";

    const path = pts.reduce((acc, point, i) => {
      if (i === 0) return `M ${point.x},${point.y}`;
      const prevPoint = pts[i - 1];
      const controlX = (prevPoint.x + point.x) / 2;
      return `${acc} C ${controlX},${prevPoint.y} ${controlX},${point.y} ${point.x},${point.y}`;
    }, "");

    return `${path} L ${width},${height} L 0,${height} Z`;
  };

  return (
    <div
      ref={containerRef}
      className="absolute inset-0"
      onMouseMove={handleMouseMove}
      style={{ cursor: sizeRef.current.width < 768 ? "default" : "none" }}
    >
      {/* Hide custom cursor on mobile for better UX */}
      <div
        className="hidden md:block fixed w-8 h-8 rounded-full mix-blend-difference bg-white pointer-events-none"
        style={{
          transform: `translate(${mousePos.x - 16}px, ${mousePos.y - 16}px)`,
          transition: "transform 0.1s ease-out",
          zIndex: 9999,
        }}
      />

      <div
        className="absolute inset-0"
        style={{
          clipPath: `path('${wavePath}')`,
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default WaveClip;
